import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";

import {
  IAbsenceTypeBalanceHistory,
  IAbsenceType,
  IAbsenceTypeHistory,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  changeStatusAbsenceType,
  createAbsenceType,
  editAbsenceType,
  fetchAbsenceType,
  fetchAbsenceTypeBalanceHistory,
  fetchAbsenceTypeHistory,
  fetchAbsenceTypes,
  fetchCompanyMainAbsenceType,
  saveCompanyMainAbsenceType,
} from "./api";
import { AbsenceQueryKeys } from "./config";

const AbsenceTypesQueries = {
  list: (companyId: string) =>
    queryOptions({
      queryKey: AbsenceQueryKeys.absenceTypesList(companyId),
      queryFn: ({ signal }) => fetchAbsenceTypes(companyId, { signal }),
      initialData: [] as IAbsenceType[],
    }),

  details: (absenceTypeId: string | undefined) =>
    queryOptions({
      enabled: !!absenceTypeId,
      queryKey: AbsenceQueryKeys.absenceTypeDetails(absenceTypeId),
      queryFn: ({ signal }) => fetchAbsenceType({ absenceTypeId, signal }),
    }),

  mainAbsenceType: (companyId: string) =>
    queryOptions({
      enabled: !!companyId,
      queryKey: AbsenceQueryKeys.mainAbsenceType(companyId),
      queryFn: ({ signal }) =>
        fetchCompanyMainAbsenceType({ companyId, signal }),
    }),

  history: (absenceTypeId: string) =>
    queryOptions<IAbsenceTypeHistory, Error>({
      enabled: !!absenceTypeId,
      queryKey: AbsenceQueryKeys.absenceTypeHistory(absenceTypeId),
      queryFn: () => fetchAbsenceTypeHistory(absenceTypeId),
    }),

  balanceHistory: (params: {
    employeeId: string;
    absenceTypeId: string;
    page: number;
    size: number;
  }) =>
    queryOptions<IAbsenceTypeBalanceHistory, Error>({
      enabled: !!params.absenceTypeId,
      queryKey: AbsenceQueryKeys.absenceTypeBalanceHistory(params),
      queryFn: ({ signal }) =>
        fetchAbsenceTypeBalanceHistory({ ...params, signal }),
    }),

  changeStatus: mutationOptions({
    mutationFn: ({
      absenceTypeId,
      active,
    }: {
      absenceTypeId: string;
      active: boolean;
    }) => changeStatusAbsenceType(absenceTypeId, active),
  }),
};

export const useListAbsenceTypes = (companyId: string) =>
  useQuery(AbsenceTypesQueries.list(companyId));

export const useGetAbsenceType = (absenceTypeId: string | undefined) =>
  useQuery(AbsenceTypesQueries.details(absenceTypeId));

export const useGetMainAbsenceType = (companyId: string) =>
  useQuery(AbsenceTypesQueries.mainAbsenceType(companyId));

export const useFetchAbsenceTypeHistory = (absenceTypeId: string) =>
  useQuery(AbsenceTypesQueries.history(absenceTypeId));

export const useFetchAbsenceTypeBalanceHistory = (params: {
  employeeId: string;
  absenceTypeId: string;
  page: number;
  size: number;
}) => useQuery(AbsenceTypesQueries.balanceHistory(params));

export const useSaveAbsenceType = (
  absenceTypeId: string | undefined,
  fullyEditable: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: IAbsenceType) =>
      absenceTypeId
        ? editAbsenceType(absenceTypeId, formData, fullyEditable)
        : createAbsenceType(formData),
    onSuccess: (resultData) =>
      Promise.all([
        queryClient.setQueryData(
          AbsenceQueryKeys.absenceTypeDetails(resultData.id),
          resultData
        ),
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.absenceTypesAll(),
        }),
      ]),
  });
};

export const useSetAbsenceTypeAsMain = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ absenceTypeId }: { absenceTypeId: string | null }) =>
      saveCompanyMainAbsenceType(companyId, absenceTypeId),
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano zmianę."));
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.absenceTypesAll(),
      });
    },
    onError: () => {
      dynamicNotification(__("Nie udało się zapisać."), "error");
    },
  });
};

export default AbsenceTypesQueries;
